import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import {
  AddReferenceHolidaysInput,
  AddReferenceHolidaysResultDto,
  CreateUpdateHolidayDto,
  HolidayCalendarOptionsDto,
  ReferenceHolidayDto,
  TenantHolidayDto,
} from 'src/core/models/settings/holidays/holiday.dto';

const HOLIDAY_CALENDAR = `${API_PREFIX}/holiday-calendar`;

const LIST_PAGE_SIZE = 1000;

/**
 * Reads and edits the tenant holiday calendar of
 * Administration > Settings > Localization > Holidays.
 */
@Injectable({ providedIn: 'root' })
export class HolidayCalendarService {
  private readonly http = inject(HttpClient);

  /** Country and year lookups plus the values to preselect. */
  getOptions(): Observable<HolidayCalendarOptionsDto> {
    return this.http.get<HolidayCalendarOptionsDto>(resolveUrl(`${HOLIDAY_CALENDAR}/options`));
  }

  /**
   * Public holidays of a country in a year, each flagged with whether the tenant already observes it.
   * Requires the holiday reference calendar feature.
   */
  getAvailable(countryCode: string, year: number): Observable<ReferenceHolidayDto[]> {
    return this.http.get<ReferenceHolidayDto[]>(resolveUrl(`${HOLIDAY_CALENDAR}/available`), {
      params: { countryCode, year },
    });
  }

  getList(): Observable<TenantHolidayDto[]> {
    return this.http
      .get<ListResponseDto<TenantHolidayDto>>(resolveUrl(HOLIDAY_CALENDAR), {
        params: {
          skipCount: 0,
          maxResultCount: LIST_PAGE_SIZE,
          filters: '[]',
          sorters: '[]',
        },
      })
      .pipe(map(response => response?.items ?? []));
  }

  /** Adds the selected reference holidays in one call. */
  addSelected(input: AddReferenceHolidaysInput): Observable<AddReferenceHolidaysResultDto> {
    return this.http.post<AddReferenceHolidaysResultDto>(
      resolveUrl(`${HOLIDAY_CALENDAR}/selected`),
      input
    );
  }

  /** Adds a holiday only this tenant observes. */
  create(input: CreateUpdateHolidayDto): Observable<TenantHolidayDto> {
    return this.http.post<TenantHolidayDto>(resolveUrl(HOLIDAY_CALENDAR), input);
  }

  /** Removes a holiday; a reference holiday becomes selectable again afterwards. */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(resolveUrl(`${HOLIDAY_CALENDAR}/${id}`));
  }
}
